import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Printer, Star, Flame, Award, Medal, Heart, Sticker, CalendarDays, Check, Mail, Sparkles, Lightbulb } from "lucide-react";
import { yearInsight } from "../lib/insight";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import Panda from "../components/Panda";
import ReportCardShare from "../components/ReportCardShare";
import YearInReview from "../components/YearInReview";
import api, { getErrorMessage } from "../lib/api";
import { STICKERS } from "../data/stickers";
import { GOAL_TIERS } from "../lib/goalStreak";

const Stat = ({ icon: Icon, label, value, color, testId }) => (
  <div className="rounded-2xl bg-[#f7fdfe] p-4 text-center" data-testid={testId}>
    <Icon size={22} className="mx-auto" style={{ color }} />
    <p className="text-2xl font-bold text-[#0c4a6e] mt-1">{value}</p>
    <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500">{label}</p>
  </div>
);

// Printable end-of-year report card for one child.
const ReportCard = () => {
  const { childId } = useParams();
  const [search] = useSearchParams();
  const [year, setYear] = useState(Number(search.get("year")) || null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [review, setReview] = useState(false);
  const [history, setHistory] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    api.get(`/children/${childId}/report-card/history`).then((r) => setHistory(r.data.years)).catch(() => setHistory([]));
  }, [childId]);

  const emailMe = async () => {
    setEmailing(true);
    try {
      const r = await api.post(`/children/${childId}/report-card/email?year=${data.year}`);
      if (r.data.sent) toast.success("Report card emailed", { description: `${data.child.name}'s ${data.year} card is on its way to your inbox.` });
      else toast.error("Email could not be delivered", { description: r.data.email_error || "" });
    } catch (e) { toast.error(getErrorMessage(e, "Could not send the email")); } finally { setEmailing(false); }
  };

  useEffect(() => {
    api.get(`/children/${childId}/report-card${year ? `?year=${year}` : ""}`).then((r) => { setData(r.data); setYear(r.data.year); }).catch(() => setError("Report card not available"));
  }, [childId, year]);

  const download = async () => {
    setBusy(true);
    try {
      const a = document.createElement("a");
      a.href = await toPng(ref.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff", skipFonts: true });
      a.download = `${data.child.name}-${data.year}-report-card.png`;
      a.click();
      toast.success("Report card downloaded");
    } catch (e) { toast.error("Could not create the image", { description: e?.message }); } finally { setBusy(false); }
  };

  if (error) return <div className="p-12 text-center" data-testid="report-card-missing">{error}. <Link to="/family" className="text-[#1b8bc9] underline">Back to the family dashboard</Link></div>;
  if (!data) return <div className="p-12 text-center text-gray-500">Loading report card...</div>;
  const badge = data.goal_badge && GOAL_TIERS.find((t) => t.tier === data.goal_badge.tier);
  const stickerName = (id) => STICKERS.find((s) => s.id === id)?.name || id;
  const prevYear = history?.find((y) => y.year === data.year - 1);
  const insight = prevYear ? yearInsight(prevYear, { ...data, year: data.year }) : null;

  return (
    <div className="bg-[#f7fdfe] min-h-[80vh] py-10 px-4" data-testid="report-card-page">
      <style>{`@media print { header, footer, .no-print { display: none !important; } @page { margin: 10mm; } body { background: #fff; } }`}</style>
      {review && <YearInReview childId={childId} year={data.year} onClose={() => setReview(false)} />}
      <div className="max-w-4xl mx-auto mb-6 flex flex-wrap items-center gap-3 no-print">
        <Link to="/family" className="inline-flex items-center gap-1 font-bold text-[#1b8bc9] hover:text-[#e5356f]" data-testid="report-card-back"><ArrowLeft size={18} /> Family dashboard</Link>
        <select value={data.year} onChange={(e) => setYear(Number(e.target.value))} className="h-9 rounded-full border-2 border-[#c9ecf5] bg-white px-3 text-sm font-bold text-[#0c4a6e]" data-testid="report-card-year">
          {data.years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={() => setReview(true)} className="inline-flex items-center gap-1 text-sm font-bold text-[#7d5fb2] hover:text-[#e5356f]" data-testid="report-card-year-in-review"><Sparkles size={16} /> Play year in review</button>
        <div className="ml-auto flex flex-wrap gap-2">
          <ReportCardShare targetRef={ref} data={data} />
          <button onClick={emailMe} disabled={emailing} className="btn-pill bg-white border-2 border-[#1b8bc9] text-[#1b8bc9] hover:bg-[#e9f9fd] inline-flex items-center gap-2 disabled:opacity-60" data-testid="report-card-email"><Mail size={18} /> {emailing ? "Sending..." : "Email me this card"}</button>
          <button onClick={download} disabled={busy} className="btn-pill bg-[#1b8bc9] hover:bg-[#156f9f] text-white inline-flex items-center gap-2 shadow disabled:opacity-60" data-testid="report-card-download"><Download size={18} /> {busy ? "Preparing..." : "Download image"}</button>
          <button onClick={() => window.print()} className="btn-pill bg-[#e5356f] hover:bg-[#c92a5e] text-white inline-flex items-center gap-2 shadow" data-testid="report-card-print"><Printer size={18} /> Print</button>
        </div>
      </div>

      <div ref={ref} className="max-w-4xl mx-auto bg-white rounded-3xl overflow-hidden border-[6px] border-[#1b8bc9]" data-testid="report-card">
        <div className="bg-[#1b8bc9] px-8 py-6 flex items-center gap-5">
          <Panda size={96} accessory={data.child.panda.accessory} scarf={data.child.panda.scarf} item="coin" />
          <div className="text-white">
            <p className="italic font-bold text-white/80">Teach your child Credit</p>
            <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Literata, serif" }} data-testid="report-card-title">{data.child.name}'s {data.year} report card</h1>
            <p className="text-white/90 text-sm mt-1">A year of learning how money and credit work - one lesson step at a time.</p>
          </div>
        </div>
        <div className="p-6 sm:p-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat icon={CalendarDays} label="active days" value={data.active_days} color="#63c79a" testId="rc-active-days" />
            <Stat icon={Flame} label="longest streak" value={`${data.longest_streak_weeks}w`} color="#f7931e" testId="rc-longest-streak" />
            <Stat icon={Star} label={`bamboo in ${data.year}`} value={data.bamboo_year} color="#f3c932" testId="rc-bamboo" />
            <Stat icon={Heart} label="cheers received" value={data.cheers_year} color="#e5356f" testId="rc-cheers" />
          </div>
          {insight && <p className="mt-3 text-sm text-[#0c4a6e] bg-[#e9f9fd] rounded-xl px-4 py-2 inline-flex items-start gap-2" data-testid="rc-insight"><Lightbulb size={16} className="mt-0.5 flex-shrink-0 text-[#f7931e]" /> <span><b>vs {data.year - 1}:</b> {insight}</span></p>}

          <h2 className="mt-8 text-lg font-bold text-[#0c4a6e]" style={{ fontFamily: "Literata, serif" }}>Lessons <span className="text-xs font-normal text-gray-500">- all-time progress</span></h2>
          <table className="mt-2 w-full text-sm" data-testid="rc-lessons">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-gray-500"><th className="py-1">Lesson</th><th className="py-1">Steps</th><th className="py-1">Quiz</th><th className="py-1">Certificate</th></tr></thead>
            <tbody>
              {data.lessons.map((l) => (
                <tr key={l.id} className="border-t border-[#e9f9fd]" data-testid={`rc-lesson-${l.id}`}>
                  <td className="py-2 font-semibold text-[#0c4a6e]">Lesson {l.num}: {l.title}</td>
                  <td className="py-2"><span className="inline-flex items-center gap-2"><span className="w-20 h-2 rounded-full bg-[#e9f9fd] overflow-hidden"><span className="block h-full bg-[#63c79a]" style={{ width: `${(l.steps_done / l.steps_total) * 100}%` }} /></span>{l.steps_done}/{l.steps_total}</span></td>
                  <td className="py-2">{l.quiz !== null ? `${l.quiz}/5` : "-"}</td>
                  <td className="py-2">{l.certified ? <span className="inline-flex items-center gap-1 text-[#8a6d00] font-bold"><Award size={14} fill="#f3c932" className="text-[#f3c932]" /> {l.certified_this_year ? `earned ${data.year}` : "earned earlier"}</span> : <span className="text-gray-400">in progress</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-gray-500">Quiz average: <b data-testid="rc-quiz-average">{data.quiz_average ?? "-"}</b>{data.quiz_average !== null && "/5"} · Certificates: <b data-testid="rc-certificates">{data.certificates_total}</b></p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-[#f3e8ff] p-4" data-testid="rc-goal">
              <p className="font-bold text-[#7d5fb2] inline-flex items-center gap-2"><Medal size={18} /> Weekly goal</p>
              {data.weekly_goal ? (
                <p className="text-sm text-[#4c1d95] mt-1">{data.weekly_goal} step{data.weekly_goal === 1 ? "" : "s"} a week · {data.goal_streak_weeks} week{data.goal_streak_weeks === 1 ? "" : "s"} in a row{badge && <span className="ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold" style={{ background: badge.bg, color: badge.color, border: `1px solid ${badge.color}` }}><Check size={12} /> {badge.label} badge</span>}</p>
              ) : <p className="text-sm text-[#4c1d95] mt-1">No weekly goal set yet.</p>}
            </div>
            <div className="rounded-2xl bg-[#fff3d6] p-4" data-testid="rc-stickers">
              <p className="font-bold text-[#7a3d00] inline-flex items-center gap-2"><Sticker size={18} /> Sticker book</p>
              <p className="text-sm text-[#7a3d00] mt-1">{data.stickers_earned} earned{data.stickers_gifted.length > 0 && <> · {data.stickers_gifted.length} gift{data.stickers_gifted.length === 1 ? "" : "s"} ({data.stickers_gifted.map((g) => `${stickerName(g.sticker_id)} from ${g.from_name}`).join(", ")})</>}{data.stickers_given.length > 0 && <> · gave {data.stickers_given.length} ({data.stickers_given.map((g) => `${stickerName(g.sticker_id)} to ${g.to_name}`).join(", ")})</>}</p>
            </div>
          </div>
          <p className="mt-6 text-[11px] text-gray-400 text-center">Generated {new Date(data.generated_at).toLocaleDateString()} · Based on the book by Ken Bossard, MBA · teachyourchildcredit.com</p>
        </div>
      </div>
    </div>
  );
};

export default ReportCard;
